import React, { type FormEvent, useEffect, useMemo, useState } from 'react'
import { User } from '../../store/slices/userSlice'
import notifyDelay from '../../functions/shared/notify-helpers/notifyDelay'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import { Navigate, useSearchParams } from 'react-router-dom'
import { REACT_APP_BSO_USER_ID } from '../../urls'
import checkDocumentsForOtherUser from '../../functions/agent/documents/checkDocumentsForOtherUser'
import { showInputAlert, swalAttentionLeave, swalError, swalSuccess } from '../../functions/swalOptions'
import LandlordHeader from '../landlordModule/LandlordHeader'
import {
    BlockedFilter,
    blockUserWithReason,
    DeliveryMode,
    ExecuteAtMode,
    FiltersState,
    hasRealPushToken,
    RecipientPreview,
    SubmitStatus,
    unblockUser,
    VerifiedFilter,
} from './eventHelpers'
import useMediaQuery from '../../hooks/useMediaQuery'
import useUnitsNew from '../../hooks/useUnitsNew'
import { formatDateTimeForTable, formatForDateTimeInput, parseDateTimeInput, parseRawExecuteAt, roundToMinute } from './landlordEvents/helpers/dateHelpers'
import { getFirstName, getInitials, interpolate } from './landlordEvents/helpers/stringHelpers'
import { buildUnitDeepLink, getUnitAddressText, isUnitActive } from './landlordEvents/helpers/unitHelpers'
import {
    DEFAULT_DROP_PRICE_BODY,
    DEFAULT_DROP_PRICE_TITLE,
    DEFAULT_ACCOUNT_BLOCKED_BODY,
    DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL,
    DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE,
    DEFAULT_ACCOUNT_BLOCKED_TITLE,
    DEFAULT_ACCOUNT_UNBLOCKED_BODY,
    DEFAULT_ACCOUNT_UNBLOCKED_TITLE,
    DEFAULT_DELAY_MS,
    DEFAULT_TRIAL_BODY,
    DEFAULT_TRIAL_DEEP_LINK,
    DEFAULT_TRIAL_DAYS,
    DEFAULT_TRIAL_TITLE,
    DEFAULT_UNIT_UPDATE_BODY,
    DEFAULT_UNIT_UPDATE_TITLE,
    EMAIL_TEMPLATE_IDS,
    PAGE_SIZE,
    defaultFilters,
    RELEASE_V2_LIVE_PREVIEW_SUBJECT,
    RELEASE_V2_LIVE_PREVIEW_BODY,
} from './landlordEvents/constants/events.constants'
import type { AnyRecord, EmailKind, UnitLike } from './landlordEvents/types/events.types'
import { updateEventsRunsHistory } from './eventsHistoryConfig'
import { runSafeEvents } from './safeRunner'
import type { RunCursor, RunCounters, RunRecord, RunStatus } from './safeTypes'
import { RootState } from '../../store/store'
import { userSlice } from '../../store/slices/userSlice'
import {
    loadActiveRunLocal,
    loadRunLocal,
    snapshotRunLocal,
    setActiveRunIdLocal,
    clearActiveRunIdLocal,
    clearRunLocal,
    acquireRunLock,
    releaseRunLock,
} from './safeStorage'
import { fetchDropPriceRecipients } from './landlordEvents/helpers/dropPriceHelpers'

const buildInitialSchedule = (): { datetime: string; iso: string } => {
    const target = roundToMinute(notifyDelay(DEFAULT_DELAY_MS))
    return { datetime: formatForDateTimeInput(target), iso: target.toISOString() }
}

const isAdminGuard = (user: Partial<User> | undefined): boolean => {
    if (!user) return false
    if (user.id === Number(REACT_APP_BSO_USER_ID)) return true
    return false
}

const statusBadgeVariantSafe: Record<RunStatus, string> = {
    running: 'primary',
    failed: 'danger',
    completed: 'success',
    cancelled: 'secondary',
}

const statusLabelSafe: Record<RunStatus, string> = {
    running: 'Running',
    failed: 'Failed',
    completed: 'Completed',
    cancelled: 'Cancelled',
}


const getReleaseV2PreviewTemplate = (kind: EmailKind): { subject: string; body: string } | null => {
    if (kind === 'basicReleaseV2' || kind === 'basicReleaseForStandardUsersV2') {
        return {
            subject: RELEASE_V2_LIVE_PREVIEW_SUBJECT,
            body: RELEASE_V2_LIVE_PREVIEW_BODY,
        }
    }
    return null
}

const getUnitDisplayNameSafe = (u: UnitLike): string => {
    const attributes = u?.attributes ?? {}
    const property = attributes?.Property?.data?.attributes ?? attributes?.Property?.attributes ?? attributes?.Property ?? {}

    const propertyNameRaw =
        property?.Name ??
        property?.Title ??
        attributes?.Name ??
        attributes?.Title ??
        u?.Name ??
        u?.Title ??
        attributes?.Marketing?.Headline ??
        'Unnamed Property'

    const areaRaw =
        property?.Area ??
        property?.Community ??
        attributes?.Area ??
        attributes?.Community ??
        u?.Area ??
        u?.Community

    const propertyName = String(propertyNameRaw ?? '').trim() || 'Unnamed Property'
    const area = String(areaRaw ?? '').trim()
    const unitNumberRaw = attributes?.Number ?? u?.Number
    const unitNumber = String(unitNumberRaw ?? '').trim()

    if (area.length > 0 && unitNumber.length > 0) return `${propertyName}, ${area}, ${unitNumber}`
    if (area.length > 0) return `${propertyName}, ${area}`
    if (unitNumber.length > 0) return `${propertyName}, ${unitNumber}`
    return propertyName
}

export default function LandlordEventsPage(): JSX.Element {
    const [searchParams] = useSearchParams()
    const dispatch = useAppDispatch()
    const currentUser = useAppSelector((state) => state.user.currentUser as User | undefined)
    if (!isAdminGuard(currentUser)) {
        return <Navigate to="/landlord/menu" replace />
    }

    const configId = useAppSelector((s: RootState) => s.app.configId)

    const eventsRunsHistory = useAppSelector((state: RootState) => state.app.eventsRunsHistory)
    const allUsers = useAppSelector((state) => state.user.allUsers as User[] | undefined) ?? []
    console.log(allUsers, "allUsers")

    const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('immediate')
    const [executeMode, setExecuteMode] = useState<ExecuteAtMode>('picker')
    const [{ datetime: initialDateTime, iso: initialIso }] = useState(buildInitialSchedule)
    const [notificationTime, setNotificationTime] = useState<string>(initialDateTime)
    const [rawExecuteAt, setRawExecuteAt] = useState<string>(initialIso)
    const [title, setTitle] = useState<string>('')
    const [description, setDescription] = useState<string>('')
    const [deepLink, setDeepLink] = useState<string>('')

    // Filters / selection
    const [filters, setFilters] = useState<FiltersState>(defaultFilters)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [selectAllActive, setSelectAllActive] = useState<boolean>(false)
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])

    useEffect(() => {
        const recipientId = Number(searchParams.get('recipientId'))
        if (!Number.isInteger(recipientId) || recipientId <= 0 || !allUsers.some((user) => user.id === recipientId)) return
        setFilters((current) => ({ ...current, id: String(recipientId) }))
        setSelectedUserIds([recipientId])
        setSelectAllActive(false)
    }, [allUsers, searchParams])

    // Status
    const [status, setStatus] = useState<SubmitStatus>('idle')
    const [statusDetails, setStatusDetails] = useState<string>('')
    const [validationError, setValidationError] = useState<string | null>(null)

    const [progress, setProgress] = useState<{ done: number; total: number }>({
        done: 0,
        total: 0,
    })

    // ---------------- EMAIL (separate fields, not tied to push) ----------------
    const [sendPushAlso, setSendPushAlso] = useState(true)
    const [sendEmailAlso, setSendEmailAlso] = useState(false)
    const [emailKind, setEmailKind] = useState<EmailKind>('universal')
    const [emailTemplateId, setEmailTemplateId] = useState(EMAIL_TEMPLATE_IDS.universal)

    const [emailAutoFill, setEmailAutoFill] = useState(true)
    const [emailDynamicData, setEmailDynamicData] = useState('')

    // email-specific fields
    const [emailTitle, setEmailTitle] = useState<string>('') // used for universal/trial preview & payload
    const [emailBody, setEmailBody] = useState<string>('') // used for universal/trial preview & payload
    const [emailDeepLink, setEmailDeepLink] = useState<string>('/agent')
    const [emailCtaLabel, setEmailCtaLabel] = useState<string>('Open')
    const [emailSupportEmail, setEmailSupportEmail] = useState<string>(DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL)
    const [emailSupportPhone, setEmailSupportPhone] = useState<string>(DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE)

    // unit_update specific fields (NOT from push)
    const [emailUnitAddress, setEmailUnitAddress] = useState<string>('')
    const [emailUnitPrice, setEmailUnitPrice] = useState<string>('')
    const [emailUnitDiscountPrice, setEmailUnitDiscountPrice] = useState<string>('')
    const [emailAdminMessage, setEmailAdminMessage] = useState<string>('')
    const [dropPriceRecipientsLoading, setDropPriceRecipientsLoading] = useState(false)
    const [dropPriceRecipientsError, setDropPriceRecipientsError] = useState<string | null>(null)
    const [dropPriceRecipientsStats, setDropPriceRecipientsStats] = useState<{ activeAds: number; totalAds: number } | null>(null)

    const [emailTrialDays, setEmailTrialDays] = useState<number>(DEFAULT_TRIAL_DAYS)
    const [blockingUserId, setBlockingUserId] = useState<number | null>(null)

    // trial affects PUSH fields (old gift switch behavior), but is driven by emailKind === 'trial'
    const prevPushRef = React.useRef<{ title: string; description: string; deepLink: string } | null>(null)

    // Units list for unit_update selection
    const unitsCatalog = useUnitsNew?.() as UnitLike[] | undefined
    const activeUnits = useMemo<UnitLike[]>(() => {
        const arr = Array.isArray(unitsCatalog) ? unitsCatalog : []
        return arr.filter(isUnitActive)
    }, [unitsCatalog])

    const [selectedUnitId, setSelectedUnitId] = useState<string>('') // for select UI

    const unitOptions = useMemo(() => {
        return activeUnits.map((u) => ({
            value: String(u?.id ?? ''),
            label: getUnitDisplayNameSafe(u),
            unitName: getUnitDisplayNameSafe(u),
            address: getUnitAddressText(u),
            unitPrice: String(u?.attributes?.Marketing?.Price ?? u?.attributes?.Marketing?.OriginalPrice ?? ''),
            unitDiscountPrice: String(u?.attributes?.Marketing?.DiscountedPrice ?? u?.attributes?.Marketing?.DiscountPrice ?? ''),
        }))
    }, [activeUnits])

    const prevFieldsRef = React.useRef<{
        title: string
        description: string
        deepLink: string
    } | null>(null)

    const [history, setHistory] = useState<RunRecord[]>([])
    const historyRef = React.useRef<RunRecord[]>([])
    const [historyLoading, setHistoryLoading] = useState(true)
    const [historyError, setHistoryError] = useState<string | null>(null)

    const [showRunDetails, setShowRunDetails] = useState(false)

    const mountedRef = React.useRef(true)
    const cancelRef = React.useRef(false)
    const runningRef = React.useRef(false)



    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    useEffect(() => {
        historyRef.current = history
    }, [history])


    useEffect(() => {
        const localRun = loadActiveRunLocal()
        if (!localRun) return

        // показываем только если незавершён
        if (localRun.status !== 'running' && localRun.status !== 'failed') return

        // если в history уже есть — обновим (а вдруг курсор дальше)
        upsertRunLocal(localRun)
        // локально не пушим в Strapi сразу — пусть будет на чекпойнте/continue
    }, [])

    useEffect(() => {
        setHistory(eventsRunsHistory)
        historyRef.current = eventsRunsHistory
        setHistoryLoading(false)
    }, [eventsRunsHistory])


    const updateFilters = (partial: Partial<FiltersState>): void => {
        setFilters((prev) => ({ ...prev, ...partial }))
    }

    const clearFilters = (): void => {
        setFilters(defaultFilters)
    }

    useEffect(() => {
        setCurrentPage(1)
    }, [filters.id, filters.name, filters.email, filters.category, filters.verified, filters.blocked, filters.hasUnitsOnly, filters.pushableOnly])


    const upsertRunLocal = (updated: RunRecord) => {
        setHistory((prev) => {
            const exists = prev.some((run) => run.runId === updated.runId)
            const next = exists ? prev.map((r) => (r.runId === updated.runId ? updated : r)) : [updated, ...prev]
            const trimmed = limitHistory(next)
            historyRef.current = trimmed
            return trimmed
        })
    }



    const persistHistory = async (nextHistory: RunRecord[]) => {
        if (!configId) {
            setHistoryError('Config ID is missing (config not loaded yet).')
            return
        }
        const res = await updateEventsRunsHistory(nextHistory, configId)
        if (res.ok) {

            setHistoryError(null)
        } else {
            setHistoryError(res.error ?? 'Failed to update backend history.')
        }
    }

    const limitHistory = (runs: RunRecord[]) => runs.slice(0, 50)

    const setHistoryAndPersist = (nextHistory: RunRecord[]) => {
        const trimmed = limitHistory(nextHistory)
        historyRef.current = trimmed
        setHistory(trimmed)
        void persistHistory(trimmed)
    }

    const upsertRunSync = (updated: RunRecord): RunRecord[] => {
        const prev = historyRef.current
        const exists = prev.some((r) => r.runId === updated.runId)

        const next = exists
            ? prev.map((r) => (r.runId === updated.runId ? updated : r))
            : [updated, ...prev]

        const trimmed = limitHistory(next)
        historyRef.current = trimmed
        setHistory(trimmed)
        return trimmed
    }

    const activeRun = useMemo<RunRecord | null>(() => {
        const unfinished = history.filter((run) => run.status === 'running' || run.status === 'failed')
        if (unfinished.length === 0) return null
        return unfinished.sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1))[0]
    }, [history])
    const hasUnfinishedRun = useMemo<boolean>(
        () => history.some((run) => run.status === 'running' || run.status === 'failed'),
        [history]
    )
    useEffect(() => {
        if (!hasUnfinishedRun) return
        const handler = (event: BeforeUnloadEvent) => {
            event.preventDefault()
            event.returnValue = ''
        }
        window.addEventListener('beforeunload', handler)
        return () => {
            window.removeEventListener('beforeunload', handler)
        }
    }, [hasUnfinishedRun])

    const recipientPool = useMemo<RecipientPreview[]>(() => {
        return allUsers.map((user) => {
            const firstName = user.ContactInfo?.FirstName ?? ''
            const familyName = user.ContactInfo?.FamilyName ?? ''
            const combinedName = [firstName, familyName].filter(Boolean).join(' ').trim()
            const displayName = combinedName || user.username || user.email || `User #${user.id}`
            const searchableName = [displayName, user.username ?? '', firstName, familyName].filter(Boolean).join(' ').toLowerCase()

            const category = typeof user.Category === 'string' ? user.Category : 'Unknown'
            const hasUnits = Array.isArray(user.Units) && user.Units.length > 0
            const isVerified = checkDocumentsForOtherUser(user.id) && user.emailVerified === true

            return {
                id: user.id,
                fullName: displayName,
                email: user.email ?? user.ContactInfo?.Email ?? '',
                phone: user.ContactInfo?.Phone ?? user.phone ?? null,
                deviceToken: user.DeviceToken ?? null,
                category,
                isVerified,
                hasUnits,
                searchableName,
                username: user.username ?? '',
                blocked: user.blocked === true,
                blockedReason: user.blockedReason ?? null,
            }
        })
    }, [allUsers])

    console.log(recipientPool, "recipientPool << recipientPool")

    const categoryOptions = useMemo<string[]>(() => {
        const set = new Set<string>()
        recipientPool.forEach((recipient) => {
            if (recipient.category) set.add(recipient.category)
        })
        return Array.from(set).sort((a, b) => a.localeCompare(b))
    }, [recipientPool])

    const filteredRecipients = useMemo<RecipientPreview[]>(() => {
        const idFilter = filters.id.trim()
        const nameFilter = filters.name.trim().toLowerCase()
        const emailFilter = filters.email.trim().toLowerCase()
        const categoryFilter = filters.category.toLowerCase()

        return recipientPool.filter((recipient) => {
            if (idFilter.length > 0 && String(recipient.id) !== idFilter) return false
            if (nameFilter && !recipient.searchableName.includes(nameFilter)) return false
            if (emailFilter && !recipient.email.toLowerCase().includes(emailFilter)) return false
            if (categoryFilter !== 'all') {
                if (recipient.category.toLowerCase() !== categoryFilter) return false
            }
            if (filters.verified === 'verified' && !recipient.isVerified) return false
            if (filters.verified === 'unverified' && recipient.isVerified) return false
            if (filters.blocked === 'blocked' && recipient.blocked !== true) return false
            if (filters.blocked === 'unblocked' && recipient.blocked === true) return false
            if (filters.hasUnitsOnly && !recipient.hasUnits) return false
            if (filters.pushableOnly && !hasRealPushToken(recipient.deviceToken)) return false
            return true
        })
    }, [recipientPool, filters])

    useEffect(() => {
        setCurrentPage((prev) => {
            const totalPages = Math.max(1, Math.ceil(filteredRecipients.length / PAGE_SIZE))
            return Math.min(prev, totalPages)
        })
    }, [filteredRecipients.length])

    const filteredIdSet = useMemo<Set<number>>(() => new Set(filteredRecipients.map((recipient) => recipient.id)), [filteredRecipients])
    const selectedIdSet = useMemo<Set<number>>(() => new Set(selectedUserIds), [selectedUserIds])

    const totalItems = filteredRecipients.length
    const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE))
    const safePage = Math.min(currentPage, totalPages)
    const pageStart = (safePage - 1) * PAGE_SIZE
    const currentPageRecipients = filteredRecipients.slice(pageStart, pageStart + PAGE_SIZE)
    const showingFrom = totalItems === 0 ? 0 : pageStart + 1
    const showingTo = totalItems === 0 ? 0 : pageStart + currentPageRecipients.length

    useEffect(() => {
        if (!selectAllActive) return
        if (filteredRecipients.length === 0) {
            setSelectedUserIds([])
            return
        }
        setSelectedUserIds(filteredRecipients.map((recipient) => recipient.id))
    }, [selectAllActive, filteredRecipients])

    const parsedFromPicker = useMemo(() => parseDateTimeInput(notificationTime), [notificationTime])
    const parsedFromRaw = useMemo(() => parseRawExecuteAt(rawExecuteAt), [rawExecuteAt])

    const scheduledDate = useMemo<Date | null>(() => {
        if (deliveryMode !== 'scheduled') return null
        return executeMode === 'picker' ? parsedFromPicker : parsedFromRaw
    }, [deliveryMode, executeMode, parsedFromPicker, parsedFromRaw])

    const relativePreview = useMemo(() => {
        if (deliveryMode !== 'scheduled' || scheduledDate == null) return ''
        const diffMs = scheduledDate.getTime() - Date.now()
        const absMs = Math.abs(diffMs)
        const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
        if (absMs < 60_000) return formatter.format(Math.round(diffMs / 1000), 'second')
        if (absMs < 3_600_000) return formatter.format(Math.round(diffMs / 60_000), 'minute')
        if (absMs < 86_400_000) return formatter.format(Math.round(diffMs / 3_600_000), 'hour')
        return formatter.format(Math.round(diffMs / 86_400_000), 'day')
    }, [deliveryMode, scheduledDate])

    const selectedRecipients = useMemo<RecipientPreview[]>(() => {
        if (selectAllActive) return filteredRecipients
        if (selectedUserIds.length === 0) return []
        const map = new Set(selectedUserIds)
        return recipientPool.filter((recipient) => map.has(recipient.id))
    }, [selectAllActive, filteredRecipients, recipientPool, selectedUserIds])

    const recipientsPreview = useMemo(() => {
        if (selectAllActive) {
            return filteredRecipients.length === 0 ? 'No recipients (filtered list is empty)' : `All filtered (${filteredRecipients.length})`
        }
        if (selectedRecipients.length === 0) return 'No recipients selected'
        if (selectedRecipients.length === 1) return selectedRecipients[0].fullName
        if (selectedRecipients.length === 2) {
            return `${selectedRecipients[0].fullName}, ${selectedRecipients[1].fullName}`
        }
        return `${selectedRecipients[0].fullName}, ${selectedRecipients[1].fullName}, +${selectedRecipients.length - 2} more`
    }, [filteredRecipients.length, selectAllActive, selectedRecipients])

    const defaultRoute = deepLink.trim() || '/agent/Units/nav'
    const summaryReady =
        (selectAllActive ? filteredRecipients.length > 0 : selectedRecipients.length > 0) &&
        (sendPushAlso || sendEmailAlso) &&
        (!sendPushAlso || (title.trim().length > 0 && description.trim().length > 0)) &&
        (!sendPushAlso || deliveryMode === 'immediate' || scheduledDate != null)

    const handleToggleAll = (checked: boolean): void => {
        if (checked) {
            if (filteredRecipients.length === 0) {
                setSelectAllActive(false)
                setSelectedUserIds([])
                return
            }
            setSelectAllActive(true)
            setSelectedUserIds(filteredRecipients.map((recipient) => recipient.id))
        } else {
            setSelectAllActive(false)
        }
    }

    const handleRecipientToggle = (userId: number): void => {
        if (selectAllActive) {
            const fallbackIds = filteredRecipients.map((recipient) => recipient.id)
            setSelectAllActive(false)
            setSelectedUserIds(() => {
                const base = new Set(fallbackIds)
                if (base.has(userId)) base.delete(userId)
                else base.add(userId)
                return Array.from(base).sort((a, b) => a - b)
            })
            return
        }

        setSelectedUserIds((prev) => {
            const base = new Set(prev)
            if (base.has(userId)) base.delete(userId)
            else base.add(userId)
            return Array.from(base).sort((a, b) => a - b)
        })
    }

    // ---------------- EMAIL payload builders ----------------
    const normalizeEmailDeepLink = (): string => {
        const v = (emailDeepLink ?? '').trim()
        return v || '/agent'
    }

    const normalizeEmailCta = (): string => {
        const v = (emailCtaLabel ?? '').trim()
        return v || 'Open'
    }

    const normalizePrice = (value: unknown): string => {
        const text = String(value ?? '').trim()
        if (!text) return 'Not specified'
        const asNumber = Number(text.replace(/,/g, ''))
        if (!Number.isFinite(asNumber)) return text
        return `AED ${asNumber.toLocaleString('en-US')}`
    }

    const buildEmailAutoPayload = (): AnyRecord => {
        const base = {
            deepLink: normalizeEmailDeepLink(),
            ctaLabel: normalizeEmailCta(),
        }

        if (emailKind === 'trial') {
            return {
                ...base,
                title: (emailTitle || DEFAULT_TRIAL_TITLE).trim(),
                description: (emailBody || DEFAULT_TRIAL_BODY).trim(),
                trialDays: Number.isFinite(emailTrialDays) ? emailTrialDays : DEFAULT_TRIAL_DAYS,
            }
        }

        if (emailKind === 'unit_update') {
            return {
                ...base,
                unitAddress: (emailUnitAddress ?? '').trim(),
                adminMessage: (emailAdminMessage ?? '').trim(),
            }
        }

        if (emailKind === 'drop_price') {
            return {
                ...base,
                unitAddress: (emailUnitAddress ?? '').trim() || 'Not specified',
                unitPrice: normalizePrice(emailUnitPrice),
                unitDiscountPrice: normalizePrice(emailUnitDiscountPrice),
                adminMessage: (emailAdminMessage ?? '').trim(),
            }
        }

        if (emailKind === 'account_blocked') {
            return {
                ...base,
                title: DEFAULT_ACCOUNT_BLOCKED_TITLE,
                description: DEFAULT_ACCOUNT_BLOCKED_BODY,
                adminMessage: (emailAdminMessage ?? '').trim(),
                supportEmail: (emailSupportEmail ?? '').trim() || DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL,
                supportPhone: (emailSupportPhone ?? '').trim() || DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE,
            }
        }

        if (emailKind === 'account_unblocked') {
            return {
                ...base,
                title: DEFAULT_ACCOUNT_UNBLOCKED_TITLE,
                description: DEFAULT_ACCOUNT_UNBLOCKED_BODY,
                supportEmail: (emailSupportEmail ?? '').trim() || DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL,
                supportPhone: (emailSupportPhone ?? '').trim() || DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE,
            }
        }

        // universal + releaseV2Warm
        return {
            ...base,
            title: (emailTitle ?? '').trim(),
            description: (emailBody ?? '').trim(),
        }
    }

    const buildDynamicForRecipient = (recipient: RecipientPreview, base: AnyRecord | undefined): AnyRecord => {
        const safeBase = (base ?? {}) as AnyRecord

        const merged: AnyRecord = {
            ...safeBase,
            firstName: safeBase.firstName ?? getFirstName(recipient.fullName),
            fullName: safeBase.fullName ?? recipient.fullName,
            deepLink: safeBase.deepLink ?? normalizeEmailDeepLink(),
            ctaLabel: safeBase.ctaLabel ?? normalizeEmailCta(),
        }

        if (emailKind === 'trial') {
            merged.title = merged.title ?? (emailTitle || DEFAULT_TRIAL_TITLE)
            merged.description = merged.description ?? (emailBody || DEFAULT_TRIAL_BODY)
            merged.trialDays = merged.trialDays ?? emailTrialDays
        }

        if (emailKind === 'unit_update') {
            merged.unitAddress = merged.unitAddress ?? emailUnitAddress
            merged.adminMessage = merged.adminMessage ?? emailAdminMessage
        }

        if (emailKind === 'drop_price') {
            merged.unitAddress = merged.unitAddress ?? (emailUnitAddress || 'Not specified')
            merged.unitPrice = merged.unitPrice ?? normalizePrice(emailUnitPrice)
            merged.unitDiscountPrice = merged.unitDiscountPrice ?? normalizePrice(emailUnitDiscountPrice)
            merged.adminMessage = merged.adminMessage ?? emailAdminMessage
        }

        if (emailKind === 'universal') {
            merged.title = merged.title ?? emailTitle
            merged.description = merged.description ?? emailBody
        }

        if (emailKind === 'account_blocked') {
            merged.title = merged.title ?? DEFAULT_ACCOUNT_BLOCKED_TITLE
            merged.description = merged.description ?? DEFAULT_ACCOUNT_BLOCKED_BODY
            merged.adminMessage = merged.adminMessage ?? emailAdminMessage
            merged.supportEmail = merged.supportEmail ?? emailSupportEmail
            merged.supportPhone = merged.supportPhone ?? emailSupportPhone
        }

        if (emailKind === 'account_unblocked') {
            merged.title = merged.title ?? DEFAULT_ACCOUNT_UNBLOCKED_TITLE
            merged.description = merged.description ?? DEFAULT_ACCOUNT_UNBLOCKED_BODY
            merged.supportEmail = merged.supportEmail ?? emailSupportEmail
            merged.supportPhone = merged.supportPhone ?? emailSupportPhone
        }

        return merged
    }

    // ---------------- EMAIL UI actions ----------------
    const onToggleEmail = (checked: boolean) => {
        setSendEmailAlso(checked)

        if (!checked) {
            // leaving email mode: if trial was active, restore push (safe)
            if (emailKind === 'trial' && prevPushRef.current) {
                const stillDefault =
                    title === DEFAULT_TRIAL_TITLE &&
                    description === DEFAULT_TRIAL_BODY &&
                    deepLink === DEFAULT_TRIAL_DEEP_LINK

                if (stillDefault) {
                    const prev = prevPushRef.current
                    setTitle(prev.title)
                    setDescription(prev.description)
                    setDeepLink(prev.deepLink)
                }
                prevPushRef.current = null
            }
            return
        }

        // init email fields from current push (helps avoid empty)
        const pushRoute = deepLink.trim() || '/agent/Units/nav'
        setEmailDeepLink(pushRoute)
        setEmailCtaLabel('Open')
        setEmailSupportEmail(DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL)
        setEmailSupportPhone(DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE)
        setEmailTitle(title.trim())
        setEmailBody(description.trim())

        // clear unit_update fields
        setEmailAdminMessage('')
        setEmailUnitAddress('')
        setEmailUnitPrice('')
        setEmailUnitDiscountPrice('')
        setSelectedUnitId('')
        setDropPriceRecipientsError(null)
        setDropPriceRecipientsStats(null)

        setEmailTrialDays(DEFAULT_TRIAL_DAYS)

        // default kind universal
        setEmailKind('universal')
        setEmailTemplateId(EMAIL_TEMPLATE_IDS.universal)
        setEmailAutoFill(true)

        const payload = (() => {
            const base = { deepLink: pushRoute, ctaLabel: 'Open' }
            return { ...base, title: title.trim(), description: description.trim() }
        })()
        setEmailDynamicData(JSON.stringify(payload, null, 2))
    }

    const onChangeEmailKind = (next: EmailKind) => {
        setEmailKind(next)
        setEmailTemplateId(EMAIL_TEMPLATE_IDS[next])
        setEmailAutoFill(true)

        // trial behaves like old "Gift Premium trial" switch
        if (next === 'trial') {
            // save current PUSH fields once
            if (!prevPushRef.current) {
                prevPushRef.current = { title, description, deepLink }
            }

            // prefill PUSH fields (editable)
            setTitle(DEFAULT_TRIAL_TITLE)
            setDescription(DEFAULT_TRIAL_BODY)
            setDeepLink(DEFAULT_TRIAL_DEEP_LINK)

            // prefill EMAIL fields for trial (independent, editable)
            setEmailTitle(DEFAULT_TRIAL_TITLE)
            setEmailBody(DEFAULT_TRIAL_BODY)
            setEmailDeepLink(normalizeEmailDeepLink() || DEFAULT_TRIAL_DEEP_LINK)
            setEmailCtaLabel(normalizeEmailCta() || 'Open')
            if (!Number.isFinite(emailTrialDays) || emailTrialDays <= 0) setEmailTrialDays(DEFAULT_TRIAL_DAYS)

            // unit_update selection reset
            setSelectedUnitId('')
            setEmailUnitAddress('')
            setEmailAdminMessage('')

            return
        }

        // leaving trial: restore PUSH if still default (meaning user didn't modify push after prefill)
        if (prevPushRef.current) {
            const stillDefault =
                title === DEFAULT_TRIAL_TITLE &&
                description === DEFAULT_TRIAL_BODY &&
                deepLink === DEFAULT_TRIAL_DEEP_LINK

            if (stillDefault) {
                const prev = prevPushRef.current
                setTitle(prev.title)
                setDescription(prev.description)
                setDeepLink(prev.deepLink)
            }
            prevPushRef.current = null
        }

        // switch-specific cleaning
        if (next === 'unit_update') {
            // for unit_update: keep separate fields (do not copy from push)
            if (!emailUnitAddress.trim()) setEmailUnitAddress('')
            setEmailUnitPrice('')
            setEmailUnitDiscountPrice('')
            if (!emailAdminMessage.trim()) setEmailAdminMessage('')
            // optional: set emailTitle/body empty because they are irrelevant for unit_update payload
            setDropPriceRecipientsError(null)
            setDropPriceRecipientsStats(null)
            return
        }

        if (next === 'drop_price') {
            setSelectedUnitId('')
            setEmailUnitAddress('')
            setEmailUnitPrice('')
            setEmailUnitDiscountPrice('')
            setEmailAdminMessage('')
            setDropPriceRecipientsError(null)
            setDropPriceRecipientsStats(null)
            return
        }

        if (next === 'account_blocked') {
            setSelectedUnitId('')
            setEmailUnitAddress('')
            setEmailUnitPrice('')
            setEmailUnitDiscountPrice('')
            setEmailTitle(DEFAULT_ACCOUNT_BLOCKED_TITLE)
            setEmailBody(DEFAULT_ACCOUNT_BLOCKED_BODY)
            setEmailSupportEmail(DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL)
            setEmailSupportPhone(DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE)
            setDropPriceRecipientsError(null)
            setDropPriceRecipientsStats(null)
            return
        }

        if (next === 'account_unblocked') {
            setSelectedUnitId('')
            setEmailUnitAddress('')
            setEmailUnitPrice('')
            setEmailUnitDiscountPrice('')
            setEmailAdminMessage('')
            setEmailTitle(DEFAULT_ACCOUNT_UNBLOCKED_TITLE)
            setEmailBody(DEFAULT_ACCOUNT_UNBLOCKED_BODY)
            setEmailSupportEmail(DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL)
            setEmailSupportPhone(DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE)
            setDropPriceRecipientsError(null)
            setDropPriceRecipientsStats(null)
            return
        }

        // universal
        // keep emailTitle/emailBody as-is
    }

    // auto-update JSON from email fields (ONLY if sendEmailAlso && emailAutoFill)
    useEffect(() => {
        if (!sendEmailAlso) return
        if (!emailAutoFill) return

        setEmailTemplateId(EMAIL_TEMPLATE_IDS[emailKind])

        const payload = buildEmailAutoPayload()
        setEmailDynamicData(JSON.stringify(payload, null, 2))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sendEmailAlso,
        emailAutoFill,
        emailKind,
        emailTitle,
        emailBody,
        emailDeepLink,
        emailCtaLabel,
        emailUnitAddress,
        emailUnitPrice,
        emailUnitDiscountPrice,
        emailAdminMessage,
        emailTrialDays,
        emailSupportEmail,
        emailSupportPhone,
    ])

    // unit select -> auto-fill unitAddress (+ optional deepLink)
    useEffect(() => {
        if (emailKind !== 'unit_update' && emailKind !== 'drop_price') return
        if (!selectedUnitId) return

        const found = unitOptions.find((o) => o.value === selectedUnitId)
        if (!found) return

        // keep unit identity consistent across UI and payloads
        setEmailUnitAddress(found.unitName ?? found.label ?? '')
        if (emailKind === 'drop_price') {
            setEmailUnitPrice(found.unitPrice ?? '')
            setEmailUnitDiscountPrice(found.unitDiscountPrice ?? '')
        }
        const nextLink = buildUnitDeepLink(selectedUnitId)

        const current = (emailDeepLink ?? '').trim()
        const looksDefault =
            current === '' ||
            current === '/agent' ||
            current === (deepLink.trim() || '/agent/Units/nav')

        if (looksDefault) setEmailDeepLink(nextLink)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUnitId, emailKind, unitOptions])

    useEffect(() => {
        if (!sendEmailAlso || emailKind !== 'drop_price') return
        if (!selectedUnitId) {
            setDropPriceRecipientsError(null)
            setDropPriceRecipientsStats(null)
            return
        }

        let cancelled = false
        const run = async (): Promise<void> => {
            const unitId = Number(selectedUnitId)
            if (!Number.isFinite(unitId) || unitId <= 0) return
            setDropPriceRecipientsLoading(true)
            setDropPriceRecipientsError(null)
            try {
                const result = await fetchDropPriceRecipients(unitId)
                if (cancelled) return
                setDropPriceRecipientsStats({ activeAds: result.activeAds, totalAds: result.totalAds })
            } catch (error: unknown) {
                if (cancelled) return
                const message = error instanceof Error ? error.message : 'Failed to resolve active advertisements for selected unit.'
                setDropPriceRecipientsError(message)
                setDropPriceRecipientsStats(null)
            } finally {
                if (!cancelled) setDropPriceRecipientsLoading(false)
            }
        }
        void run()
        return () => { cancelled = true }
    }, [sendEmailAlso, emailKind, selectedUnitId])

    const handleResetForm = (): void => {
        const nextSchedule = buildInitialSchedule()
        setDeliveryMode('immediate')
        setExecuteMode('picker')
        setNotificationTime(nextSchedule.datetime)
        setRawExecuteAt(nextSchedule.iso)
        setTitle('')
        setDescription('')
        setDeepLink('')
        setSendPushAlso(true)
        setStatus('idle')
        setStatusDetails('')
        setValidationError(null)
        setSelectAllActive(false)
        setSelectedUserIds([])
        clearFilters()
        setProgress({ done: 0, total: 0 })

        // reset email
        setSendEmailAlso(false)
        setEmailKind('universal')
        setEmailTemplateId(EMAIL_TEMPLATE_IDS.universal)
        setEmailDynamicData('')
        setEmailAutoFill(true)

        setEmailTitle('')
        setEmailBody('')
        setEmailDeepLink('/agent')
        setEmailCtaLabel('Open')
        setEmailSupportEmail(DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL)
        setEmailSupportPhone(DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE)
        setEmailUnitAddress('')
        setEmailUnitPrice('')
        setEmailUnitDiscountPrice('')
        setEmailAdminMessage('')
        setEmailTrialDays(DEFAULT_TRIAL_DAYS)
        setDropPriceRecipientsLoading(false)
        setDropPriceRecipientsError(null)
        setDropPriceRecipientsStats(null)

        setSelectedUnitId('')

        // reset trial push restore ref
        prevPushRef.current = null
    }

    // ---------------- Preview (admin) ----------------
    const previewRecipient = useMemo<RecipientPreview | null>(() => {
        if (!sendEmailAlso) return null
        if (selectAllActive) {
            return filteredRecipients[0] ?? null
        }
        return selectedRecipients[0] ?? null
    }, [sendEmailAlso, selectAllActive, filteredRecipients, selectedRecipients])

    const parsedEmailJsonForPreview = useMemo<AnyRecord | undefined>(() => {
        if (!sendEmailAlso) return undefined
        if (!emailDynamicData.trim()) return buildEmailAutoPayload()
        try {
            return JSON.parse(emailDynamicData.trim())
        } catch {
            // если JSON сломан — превью всё равно можно показать из полей
            return buildEmailAutoPayload()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendEmailAlso, emailDynamicData, emailKind, emailTitle, emailBody, emailUnitAddress, emailUnitPrice, emailUnitDiscountPrice, emailAdminMessage, emailTrialDays, emailDeepLink, emailCtaLabel, emailSupportEmail, emailSupportPhone])

    const emailPreview = useMemo(() => {
        if (!sendEmailAlso) return null
        if (!previewRecipient) return { subject: '—', body: 'Select at least one recipient to see preview.' }

        const dyn = buildDynamicForRecipient(previewRecipient, parsedEmailJsonForPreview)
        const releaseV2Template = getReleaseV2PreviewTemplate(emailKind)

        if (emailKind === 'unit_update') {
            const subject = interpolate(DEFAULT_UNIT_UPDATE_TITLE, dyn)
            const body = interpolate(DEFAULT_UNIT_UPDATE_BODY, dyn)
            return { subject, body }
        }
        if (emailKind === 'drop_price') {
            return {
                subject: interpolate(DEFAULT_DROP_PRICE_TITLE, dyn),
                body: interpolate(DEFAULT_DROP_PRICE_BODY, dyn),
            }
        }
        if (emailKind === 'account_blocked') {
            return {
                subject: interpolate(DEFAULT_ACCOUNT_BLOCKED_TITLE, dyn),
                body: interpolate(DEFAULT_ACCOUNT_BLOCKED_BODY, dyn),
            }
        }
        if (emailKind === 'account_unblocked') {
            return {
                subject: interpolate(DEFAULT_ACCOUNT_UNBLOCKED_TITLE, dyn),
                body: interpolate(DEFAULT_ACCOUNT_UNBLOCKED_BODY, dyn),
            }
        }
        if (releaseV2Template) {
            return {
                subject: interpolate(releaseV2Template.subject, dyn),
                body: interpolate(releaseV2Template.body, dyn),
            }
        }

        if (emailKind === 'trial') {
            const subjTpl = (dyn?.title ?? DEFAULT_TRIAL_TITLE) as string
            const bodyTpl = (dyn?.description ?? DEFAULT_TRIAL_BODY) as string
            return { subject: interpolate(subjTpl, dyn), body: interpolate(bodyTpl, dyn) }
        }

        // universal + releaseV2Warm
        const subjTpl = (dyn?.title ?? '') as string
        const bodyTpl = (dyn?.description ?? '') as string
        return { subject: interpolate(subjTpl, dyn), body: interpolate(bodyTpl, dyn) }
    }, [sendEmailAlso, previewRecipient, parsedEmailJsonForPreview, emailKind])


    const finalEmailPreview = useMemo(() => {
        if (!sendEmailAlso || !previewRecipient) return null

        const base =
            emailDynamicData.trim()
                ? (() => {
                    try {
                        return JSON.parse(emailDynamicData)
                    } catch {
                        return buildEmailAutoPayload()
                    }
                })()
                : buildEmailAutoPayload()

        const dyn = buildDynamicForRecipient(previewRecipient, base)
        const releaseV2Template = getReleaseV2PreviewTemplate(emailKind)

        if (emailKind === 'unit_update') {
            return {
                subject: interpolate(DEFAULT_UNIT_UPDATE_TITLE, dyn),
                body: interpolate(DEFAULT_UNIT_UPDATE_BODY, dyn),
            }
        }
        if (emailKind === 'drop_price') {
            return {
                subject: interpolate(DEFAULT_DROP_PRICE_TITLE, dyn),
                body: interpolate(DEFAULT_DROP_PRICE_BODY, dyn),
            }
        }
        if (emailKind === 'account_blocked') {
            return {
                subject: interpolate(DEFAULT_ACCOUNT_BLOCKED_TITLE, dyn),
                body: interpolate(DEFAULT_ACCOUNT_BLOCKED_BODY, dyn),
            }
        }
        if (emailKind === 'account_unblocked') {
            return {
                subject: interpolate(DEFAULT_ACCOUNT_UNBLOCKED_TITLE, dyn),
                body: interpolate(DEFAULT_ACCOUNT_UNBLOCKED_BODY, dyn),
            }
        }
        if (releaseV2Template) {
            return {
                subject: interpolate(releaseV2Template.subject, dyn),
                body: interpolate(releaseV2Template.body, dyn),
            }
        }

        const subjectTpl =
            emailKind === 'trial'
                ? dyn.title ?? DEFAULT_TRIAL_TITLE
                : dyn.title ?? ''

        const bodyTpl =
            emailKind === 'trial'
                ? dyn.description ?? DEFAULT_TRIAL_BODY
                : dyn.description ?? ''

        return {
            subject: interpolate(subjectTpl, dyn),
            body: interpolate(bodyTpl, dyn),
        }
    }, [
        sendEmailAlso,
        previewRecipient,
        emailDynamicData,
        emailKind,
        emailTitle,
        emailBody,
        emailUnitAddress,
        emailUnitPrice,
        emailUnitDiscountPrice,
        emailAdminMessage,
        emailTrialDays,
        emailSupportEmail,
        emailSupportPhone,
    ])



    const buildCounters = (): RunCounters => ({
        trialOk: 0,
        trialError: 0,
        pushOk: 0,
        pushSkip: 0,
        pushError: 0,
        emailOk: 0,
        emailSkip: 0,
        emailError: 0,
    })

    const buildRecipientsByIds = (ids: number[]): { recipients: RecipientPreview[]; missingIds: number[] } => {
        const map = new Map(recipientPool.map((recipient) => [recipient.id, recipient]))
        const recipients: RecipientPreview[] = []
        const missingIds: number[] = []
        ids.forEach((id) => {
            const recipient = map.get(id)
            if (recipient) recipients.push(recipient)
            else missingIds.push(id)
        })
        return { recipients, missingIds }
    }
    const lastPersistAtRef = React.useRef<number>(0)
    const lastCheckpointIndexRef = React.useRef<number>(-1)
    const PERSIST_THROTTLE_MS = 5000
    const CHECKPOINT_EVERY_N = 300



    const persistHistoryThrottled = async (nextHistory: RunRecord[], opts?: { force?: boolean; checkpointIndex?: number }) => {
        const force = opts?.force === true
        const checkpointIndex = typeof opts?.checkpointIndex === 'number' ? opts!.checkpointIndex : null

        if (!force && checkpointIndex != null) {
            const last = lastCheckpointIndexRef.current
            if (checkpointIndex <= 0) return
            if (checkpointIndex % CHECKPOINT_EVERY_N !== 0) return
            if (checkpointIndex === last) return
            lastCheckpointIndexRef.current = checkpointIndex
        }

        const now = Date.now()
        if (!force && now - lastPersistAtRef.current < PERSIST_THROTTLE_MS) return
        lastPersistAtRef.current = now

        await persistHistory(nextHistory)
    }

    const runWithHistory = async (run: RunRecord): Promise<RunRecord> => {
        cancelRef.current = false
        runningRef.current = true

        setStatus('sending')
        setStatusDetails('')
        const { recipients, missingIds } = buildRecipientsByIds(run.recipients.ids)
        setProgress({ done: run.cursor.index, total: run.recipients.count })

        if (missingIds.length > 0) {
            runningRef.current = false
            const message = `Recipients are not loaded yet (${missingIds.length} missing). Please refresh users list and continue.`
            const failedRun: RunRecord = {
                ...run,
                status: 'failed',
                lastError: message,
                statusMessage: message,
                completedAt: new Date().toISOString(),
            }
            const nextHistory = upsertRunSync(failedRun)
            snapshotRunLocal(failedRun)
            setActiveRunIdLocal(failedRun.runId)
            releaseRunLock(failedRun.runId)
            void persistHistoryThrottled(nextHistory, { force: true })
            setStatus('error')
            setStatusDetails(message)
            return failedRun
        }

        const finalRun = await runSafeEvents({
            run,
            recipients,
            updateRun: async (next) => {
                if (!mountedRef.current) return

                const nextHistory = upsertRunSync(next)

                snapshotRunLocal(next)
                setActiveRunIdLocal(next.runId)


                const isFinal = next.status === 'completed' || next.status === 'cancelled' || next.status === 'failed'

                await persistHistoryThrottled(nextHistory, {
                    force: isFinal,
                    checkpointIndex: next.cursor?.index ?? null,
                })
            },
            onProgress: (update) => {
                if (!mountedRef.current) return
                setProgress({ done: update.done, total: update.total })
                setStatusDetails(update.statusMessage)
                // local snapshot на каждый тик уже делает runner, тут не нужно
            },
            isCancelled: () => cancelRef.current || !mountedRef.current,
            checkpointEveryN: 300,
        })
        runningRef.current = false

        if (!mountedRef.current) return finalRun

        if (finalRun.status === 'completed') {
            setStatus('success')
            setStatusDetails(finalRun.statusMessage ?? 'Completed.')
            setProgress({ done: finalRun.recipients.count, total: finalRun.recipients.count })
            handleResetForm()
            clearFilters()
        } else if (finalRun.status === 'failed') {
            setStatus('error')
            setStatusDetails(finalRun.lastError ?? finalRun.statusMessage ?? 'Run failed.')
        } else if (finalRun.status === 'cancelled') {
            setStatus('idle')
            setStatusDetails(finalRun.statusMessage ?? 'Run cancelled.')
        }
        if (finalRun.status === 'completed' || finalRun.status === 'failed' || finalRun.status === 'cancelled') {
            clearRunLocal(finalRun.runId)
            clearActiveRunIdLocal()
            releaseRunLock(finalRun.runId)
        }
        return finalRun
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        setValidationError(null)
        setStatus('idle')
        setStatusDetails('')

        const normalizedTitle = title.trim()
        const normalizedDescription = description.trim()
        const normalizedRoute = deepLink.trim() || '/agent/Units/nav'
        const messageBody = normalizedDescription

        if (hasUnfinishedRun) {
            setValidationError('An active run is already in progress. Continue or cancel it first.')
            return
        }

        // push audience must remain independent from drop_price email filtering
        const baseRecipients = selectAllActive ? filteredRecipients : selectedRecipients
        const uniqueBaseRecipients = Array.from(new Map(baseRecipients.map((recipient) => [recipient.id, recipient])).values())
        let dropPriceEmailRecipients: RecipientPreview[] = []

        if (sendEmailAlso && emailKind === 'drop_price') {
            const unitId = Number(selectedUnitId)
            if (!Number.isFinite(unitId) || unitId <= 0) {
                setValidationError('Select a valid unit for DropPrice event.')
                return
            }
            setDropPriceRecipientsLoading(true)
            setDropPriceRecipientsError(null)
            try {
                const result = await fetchDropPriceRecipients(unitId)
                setDropPriceRecipientsStats({ activeAds: result.activeAds, totalAds: result.totalAds })
                const recipientMap = new Map(recipientPool.map((recipient) => [recipient.id, recipient]))
                const resolved = result.userIds.map((id) => recipientMap.get(id)).filter(Boolean) as RecipientPreview[]
                dropPriceEmailRecipients = Array.from(new Map(resolved.map((recipient) => [recipient.id, recipient])).values())
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : 'Failed to resolve DropPrice recipients.'
                setDropPriceRecipientsError(message)
                setValidationError(message)
                return
            } finally {
                setDropPriceRecipientsLoading(false)
            }
        }

        const dropPriceActiveRecipientIds = sendEmailAlso && emailKind === 'drop_price'
            ? Array.from(new Set(dropPriceEmailRecipients.map((recipient) => recipient.id)))
            : []

        if ((sendPushAlso || sendEmailAlso) && uniqueBaseRecipients.length === 0) {
            setValidationError('Select recipients or apply a valid filter.')
            return
        }
        if (!sendPushAlso && !sendEmailAlso) {
            setValidationError('Enable at least one channel: Push or Email.')
            return
        }
        if (sendPushAlso && !normalizedTitle) {
            setValidationError('Title is required.')
            return
        }
        if (sendPushAlso && !normalizedDescription) {
            setValidationError('Description is required.')
            return
        }

        let parsedDynamicTemplateData: AnyRecord | undefined

        if (sendEmailAlso) {
            const derivedTemplateId = EMAIL_TEMPLATE_IDS[emailKind]
            if (!derivedTemplateId?.trim()) {
                setValidationError('Email template ID is missing for selected email type.')
                return
            }

            // validation for unit_update: needs emailAdminMessage + emailUnitAddress
            if (emailKind === 'unit_update') {
                if (!emailUnitAddress.trim()) {
                    setValidationError('Unit address is required for unit_update email.')
                    return
                }
                if (!emailAdminMessage.trim()) {
                    setValidationError('Admin message is required for unit_update email.')
                    return
                }
            }
            if (emailKind === 'drop_price') {
                if (!selectedUnitId) {
                    setValidationError('Select unit for DropPrice email.')
                    return
                }
                if (!emailUnitAddress.trim()) {
                    setValidationError('Unit address is required for DropPrice email.')
                    return
                }
                if (!String(emailUnitPrice ?? '').trim()) {
                    setValidationError('Old unit price is required for DropPrice email.')
                    return
                }
                if (!String(emailUnitDiscountPrice ?? '').trim()) {
                    setValidationError('New authorized unit price is required for DropPrice email.')
                    return
                }
            }

            if (emailKind === 'account_blocked') {
                if (!emailAdminMessage.trim()) {
                    setValidationError('Admin message is required for account_blocked email.')
                    return
                }
            }

            // validation for universal: needs emailTitle/emailBody
            if (emailKind === 'universal') {
                if (!emailTitle.trim()) {
                    setValidationError('Email title is required for universal email.')
                    return
                }
                if (!emailBody.trim()) {
                    setValidationError('Email description/body is required for universal email.')
                    return
                }
            }

            // validation for trial
            if (emailKind === 'trial') {
                if (!Number.isFinite(emailTrialDays) || emailTrialDays <= 0) {
                    setValidationError('Trial days must be a valid number (> 0).')
                    return
                }
            }

            if (emailDynamicData.trim()) {
                try {
                    parsedDynamicTemplateData = JSON.parse(emailDynamicData.trim())
                } catch {
                    setValidationError('Dynamic template data must be valid JSON.')
                    return
                }
            } else {
                parsedDynamicTemplateData = buildEmailAutoPayload()
            }
        }

        let executeAt: Date | null = null
        if (sendPushAlso && deliveryMode === 'scheduled') {
            executeAt = scheduledDate
            if (executeAt == null || Number.isNaN(executeAt.getTime())) {
                setValidationError('Specify a valid send time.')
                return
            }
            if (executeAt.getTime() < Date.now() - 30_000) {
                setValidationError('Send time cannot be in the past.')
                return
            }
        }

        const entryId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        const baseRecipientsLabel = selectAllActive ? `Filtered selection (${uniqueBaseRecipients.length})` : `${uniqueBaseRecipients.length} recipient(s)`

        const sure = await swalAttentionLeave('Are you shure to sent this notify? ')
        if (!sure.isConfirmed) {
            setStatus('idle')
            setStatusDetails('Sending cancelled.')
            setProgress({ done: 0, total: 0 })
            return
        }

        const totalRecipientsForProgress = uniqueBaseRecipients.length
        setProgress({ done: 0, total: totalRecipientsForProgress })

        const snapshotEmailTitle = emailKind === 'trial' ? (emailTitle || DEFAULT_TRIAL_TITLE).trim() : emailTitle.trim()
        const snapshotEmailBody = emailKind === 'trial' ? (emailBody || DEFAULT_TRIAL_BODY).trim() : emailBody.trim()
        const snapshotDeepLink = normalizeEmailDeepLink()
        const snapshotCta = normalizeEmailCta()

        const dropPriceRecipientFilter: RunRecord['payload']['email']['recipientFilter'] =
            sendEmailAlso && emailKind === 'drop_price'
                ? { mode: 'drop_price_active_ad', recipientIds: dropPriceActiveRecipientIds }
                : undefined

        const payloadSnapshot: RunRecord['payload'] = {
            push: {
                enabled: sendPushAlso,
                title: normalizedTitle,
                description: messageBody,
                deepLink: normalizedRoute,
                deliveryMode,
                scheduledFor: executeAt?.toISOString() ?? null,
            },
            email: {
                enabled: sendEmailAlso,
                kind: emailKind,
                templateId: EMAIL_TEMPLATE_IDS[emailKind].trim(),
                dynamicTemplateData: parsedDynamicTemplateData ?? buildEmailAutoPayload(),
                recipientFilter: dropPriceRecipientFilter,
                fields: {
                    title: snapshotEmailTitle,
                    body: snapshotEmailBody,
                    unitAddress: emailUnitAddress.trim(),
                    unitPrice: normalizePrice(emailUnitPrice),
                    unitDiscountPrice: normalizePrice(emailUnitDiscountPrice),
                    adminMessage: emailAdminMessage.trim(),
                    trialDays: emailTrialDays,
                    deepLink: snapshotDeepLink,
                    ctaLabel: snapshotCta,
                    termsUrl: '',
                    supportEmail: (emailSupportEmail ?? '').trim() || DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL,
                    supportPhone: (emailSupportPhone ?? '').trim() || DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE,
                },
            },
        }
        const makeHistoryEntry = (
            runId: string,
            recipients: RecipientPreview[],
            recipientsLabel: string,
            payload: RunRecord['payload']
        ): RunRecord => {
            const shouldGrantTrialForRun = payload.email.enabled && payload.email.kind === 'trial'
            const initialStep: RunCursor['step'] = payload.push.enabled ? 'push' : shouldGrantTrialForRun ? 'trial' : 'email'
            const initialCursor: RunCursor = { step: initialStep, index: 0 }

            return {
                runId,
                createdAt: new Date().toISOString(),
                createdByAdminId: currentUser?.id,
                status: 'running',
                recipients: {
                    ids: recipients.map((recipient) => recipient.id),
                    label: recipientsLabel,
                    count: recipients.length,
                },
                payload,
                cursor: initialCursor,
                counters: buildCounters(),
                statusMessage: deliveryMode === 'scheduled' ? 'Scheduled locally (no server queue).' : 'Running...',
            }
        }

        const historyEntry = makeHistoryEntry(
            entryId,
            uniqueBaseRecipients,
            baseRecipientsLabel,
            payloadSnapshot
        )

        if (!acquireRunLock(historyEntry.runId)) {
            setValidationError('Another run is locked in this browser. Close/cancel it first.')
            return
        }

        snapshotRunLocal(historyEntry)
        setActiveRunIdLocal(historyEntry.runId)
        setHistoryAndPersist([historyEntry, ...historyRef.current])
        setStatus('sending')

        try {
            await runWithHistory(historyEntry)
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to run notification.'
            const failedRun: RunRecord = {
                ...historyEntry,
                status: 'failed',
                lastError: errorMessage,
                statusMessage: errorMessage,
            }
            const nextHistory = upsertRunSync(failedRun)
            snapshotRunLocal(failedRun)
            setActiveRunIdLocal(failedRun.runId)
            releaseRunLock(failedRun.runId)
            void persistHistoryThrottled(nextHistory, { force: true })
            setStatus('error')
            setStatusDetails(errorMessage)
        }
    }

    const syncRecipientBlockedState = (recipient: RecipientPreview, blocked: boolean, blockedReason: string | null) => {
        const updatedAllUsers = allUsers.map((user) =>
            user.id === recipient.id
                ? {
                    ...user,
                    blocked,
                    blockedReason,
                }
                : user
        )
        dispatch(userSlice.actions.allUsersFetchingSuccess(updatedAllUsers))
    }

    const handleBlockUser = async (recipient: RecipientPreview): Promise<void> => {
        if (blockingUserId != null) return

        const input = await showInputAlert(`Are you sure you want to block ${recipient.fullName}?`, '', {
            icon: 'warning',
            compactButtons: true,
            reverseButtons: true,
            confirmText: 'Block User',
            cancelText: 'Cancel',
        })
        if (!input.isConfirmed) return

        const reason = String(input.value?.status ?? '').trim()
        if (!reason) {
            setValidationError('Blocking reason is required.')
            return
        }

        setBlockingUserId(recipient.id)
        setValidationError(null)

        try {
            const updateResult = await blockUserWithReason(recipient.id, reason)
            if (!updateResult.ok) {
                const message = updateResult.error ?? 'Failed to update user block status.'
                setValidationError(message)
                await swalError(message)
                return
            }

            syncRecipientBlockedState(recipient, true, reason)
            await swalSuccess('User was blocked successfully.')
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to block user.'
            setValidationError(message)
            await swalError(message)
        } finally {
            setBlockingUserId(null)
        }
    }

    const handleUnblockUser = async (recipient: RecipientPreview): Promise<void> => {
        if (blockingUserId != null) return

        const confirmation = await swalAttentionLeave(
            `Are you sure you want to unblock ${recipient.fullName}?`,
            'Confirm Unblock User',
            'Unblock User',
            {
                icon: 'warning',
                compactButtons: true,
                reverseButtons: true,
                cancelText: 'Cancel',
            }
        )
        if (!confirmation.isConfirmed) return

        setBlockingUserId(recipient.id)
        setValidationError(null)

        try {
            const updateResult = await unblockUser(recipient.id)
            if (!updateResult.ok) {
                const message = updateResult.error ?? 'Failed to update user unblocked status.'
                setValidationError(message)
                await swalError(message)
                return
            }

            syncRecipientBlockedState(recipient, false, null)
            await swalSuccess('User was unblocked successfully.')
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to unblock user.'
            setValidationError(message)
            await swalError(message)
        } finally {
            setBlockingUserId(null)
        }
    }

    const handleToggleBlockedState = async (recipient: RecipientPreview): Promise<void> => {
        if (recipient.blocked) {
            await handleUnblockUser(recipient)
            return
        }

        await handleBlockUser(recipient)
    }

    const handleContinueRun = async (): Promise<void> => {
        if (!activeRun || runningRef.current) return

        const local = loadRunLocal(activeRun.runId)
        const base = local ?? activeRun

        const runToResume: RunRecord = {
            ...base,
            status: 'running',
            statusMessage: 'Resuming run...',
        }

        snapshotRunLocal(runToResume)
        setActiveRunIdLocal(runToResume.runId)

        if (!acquireRunLock(runToResume.runId)) {
            setStatus('error')
            setStatusDetails('Another run is locked in this browser. Cancel it first.')
            return
        }

        upsertRunSync(runToResume)
        await runWithHistory(runToResume)
    }

    const handleCancelRun = (): void => {
        if (!activeRun) return
        cancelRef.current = true

        if (!runningRef.current) {
            const cancelledRun: RunRecord = {
                ...activeRun,
                status: 'cancelled',
                statusMessage: 'Cancelled by admin.',
                completedAt: new Date().toISOString(),
            }

            const nextHistory = upsertRunSync(cancelledRun)
            snapshotRunLocal(cancelledRun)


            clearRunLocal(cancelledRun.runId)
            clearActiveRunIdLocal()
            releaseRunLock(cancelledRun.runId)

            void persistHistoryThrottled(nextHistory, { force: true })

            setStatus('idle')
            setStatusDetails('Run cancelled.')
        }
    }

    const isDesktop = useMediaQuery('(min-width: 992px)')
    const isMobileCanvas = useMediaQuery('(max-width: 480px)')
    const isTabletUp = useMediaQuery('(min-width: 576px)')
    const isWide = isDesktop || isTabletUp

    return (
        <div className="page d-flex flex-column flex-row-fluid fs-5">
            {!isDesktop && <LandlordHeader active="Events" />}

            <div className={isMobileCanvas ? '' : `w-100 bg-white h-auto shadow-sm card border-0 ${isWide ? 'events-shell--wide' : 'mx-auto'}`}>
                <div className="container-fluid py-10">
                    {historyError && (
                        <div className="alert alert-danger d-flex align-items-center mb-6">
                            <span className="svg-icon svg-icon-2hx svg-icon-danger me-3">
                                <i className="bi bi-exclamation-triangle" />
                            </span>
                            <span>{historyError}</span>
                        </div>
                    )}

                    {activeRun && (
                        <div className="alert alert-warning d-flex flex-column gap-3 mb-6">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                <div>
                                    <div className="fw-semibold">Unfinished run detected</div>
                                    <div className="text-muted fs-7">
                                        Status: {statusLabelSafe[activeRun.status]} · Created {formatDateTimeForTable(activeRun.createdAt)}
                                    </div>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    <button type="button" className="btn btn-light-primary" onClick={handleContinueRun} disabled={runningRef.current}>
                                        Continue
                                    </button>
                                    <button type="button" className="btn btn-light-danger" onClick={handleCancelRun}>
                                        Cancel
                                    </button>
                                    <button type="button" className="btn btn-light" onClick={() => setShowRunDetails((prev) => !prev)}>
                                        {showRunDetails ? 'Hide details' : 'View details'}
                                    </button>
                                </div>
                            </div>
                            {showRunDetails && (
                                <div className="border rounded p-3 bg-white">
                                    <div className="fs-8 text-muted text-uppercase">Recipients</div>
                                    <div className="mb-2">{activeRun.recipients.label}</div>
                                    <div className="fs-8 text-muted text-uppercase">Cursor</div>
                                    <div className="mb-2">
                                        Step: {activeRun.cursor.step} · Next index: {activeRun.cursor.index}
                                    </div>
                                    <div className="fs-8 text-muted text-uppercase">Last message</div>
                                    <div>{activeRun.statusMessage ?? '—'}</div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="row g-5">
                        <div className="col-12 col-xxl-8">
                            <div className="card card-flush h-100">
                                <div className="card-header border-0 pt-6">
                                    <div className="card-title flex-column">
                                        <h2 className="fw-bold mb-1">Notify Events</h2>
                                        <div className="text-muted fs-7">Configure the event, filter recipients, and confirm sending.</div>
                                    </div>
                                </div>

                                <div className="pt-0 p-2">
                                    {/* FILTERS */}
                                    <div className="border rounded p-4 mb-8">
                                        <div className="row g-4">
                                            <div className="col-lg-2 col-md-4">
                                                <label className="form-label fw-semibold">ID</label>
                                                <input className="form-control form-control-sm" type="text" value={filters.id} onChange={(event) => updateFilters({ id: event.target.value })} placeholder="294" />
                                            </div>

                                            <div className="col-lg-3 col-md-4">
                                                <label className="form-label fw-semibold">Name</label>
                                                <input className="form-control form-control-sm" type="text" value={filters.name} onChange={(event) => updateFilters({ name: event.target.value })} placeholder="Search by name" />
                                            </div>

                                            <div className="col-lg-3 col-md-4">
                                                <label className="form-label fw-semibold">Email</label>
                                                <input className="form-control form-control-sm" type="text" value={filters.email} onChange={(event) => updateFilters({ email: event.target.value })} placeholder="user@bso.ae" />
                                            </div>

                                            <div className="col-lg-2 col-md-4">
                                                <label className="form-label fw-semibold">Category</label>
                                                <select className="form-select form-select-sm" value={filters.category} onChange={(event) => updateFilters({ category: event.target.value })}>
                                                    <option value="all">All</option>
                                                    {categoryOptions.map((option) => (
                                                        <option key={option} value={option.toLowerCase()}>
                                                            {option}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="col-lg-2 col-md-4">
                                                <label className="form-label fw-semibold">Verified</label>
                                                <select className="form-select form-select-sm" value={filters.verified} onChange={(event) => updateFilters({ verified: event.target.value as VerifiedFilter })}>
                                                    <option value="all">All</option>
                                                    <option value="verified">Verified only</option>
                                                    <option value="unverified">Not verified</option>
                                                </select>
                                            </div>

                                            <div className="col-lg-2 col-md-4">
                                                <label className="form-label fw-semibold">Blocked</label>
                                                <select className="form-select form-select-sm" value={filters.blocked} onChange={(event) => updateFilters({ blocked: event.target.value as BlockedFilter })}>
                                                    <option value="all">All</option>
                                                    <option value="blocked">Blocked only</option>
                                                    <option value="unblocked">Unblocked only</option>
                                                </select>
                                            </div>

                                            <div className="col-lg-2 col-md-4 d-flex align-items-end">
                                                <div className="form-check form-switch form-check-custom form-check-solid">
                                                    <input className="form-check-input" type="checkbox" id="events-has-units" checked={filters.hasUnitsOnly} onChange={(event) => updateFilters({ hasUnitsOnly: event.target.checked })} />
                                                    <label className="form-check-label" htmlFor="events-has-units">
                                                        Has units only
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="col-lg-2 col-md-4 d-flex align-items-end">
                                                <div className="form-check form-switch form-check-custom form-check-solid">
                                                    <input className="form-check-input" type="checkbox" id="events-pushable-only" checked={filters.pushableOnly} onChange={(e) => setFilters((prev) => ({ ...prev, pushableOnly: e.target.checked }))} />
                                                    <label className="form-check-label" htmlFor="events-pushable-only">
                                                        Push-enabled only
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-end gap-3 mt-4">
                                            <button type="button" className="btn btn-light btn-sm" onClick={clearFilters}>
                                                Clear filters
                                            </button>
                                        </div>
                                    </div>

                                    <form onSubmit={handleSubmit} noValidate>
                                        {/* SELECT ALL + PAGINATION */}
                                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="form-check form-check-custom form-check-solid">
                                                    <input className="form-check-input" type="checkbox" id="events-select-all" checked={selectAllActive} onChange={(event) => handleToggleAll(event.target.checked)} disabled={filteredRecipients.length === 0} />
                                                    <label className="form-check-label text-muted" htmlFor="events-select-all">
                                                        Select all filtered
                                                    </label>
                                                </div>

                                                <span className="badge badge-light-primary">
                                                    {showingFrom}-{showingTo} of {totalItems}
                                                </span>
                                            </div>

                                            <nav aria-label="Recipients pagination">
                                                <ul className="pagination pagination-sm mb-0">
                                                    <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                                                        <button type="button" className="page-link" onClick={() => setCurrentPage(1)}>
                                                            First
                                                        </button>
                                                    </li>
                                                    <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                                                        <button type="button" className="page-link" onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}>
                                                            Prev
                                                        </button>
                                                    </li>
                                                    <li className="page-item disabled">
                                                        <span className="page-link">
                                                            Page {safePage}/{totalPages}
                                                        </span>
                                                    </li>
                                                    <li className={`page-item ${safePage === totalPages ? 'disabled' : ''}`}>
                                                        <button type="button" className="page-link" onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}>
                                                            Next
                                                        </button>
                                                    </li>
                                                    <li className={`page-item ${safePage === totalPages ? 'disabled' : ''}`}>
                                                        <button type="button" className="page-link" onClick={() => setCurrentPage(totalPages)}>
                                                            Last
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>

                                        {/* RECIPIENTS */}
                                        <div className="mb-8">
                                            {currentPageRecipients.length === 0 ? (
                                                <div className="text-center text-muted py-10">Nothing found for the selected filters.</div>
                                            ) : (
                                                <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
                                                    {currentPageRecipients.map((recipient) => {
                                                        console.log(recipient, "recipient <<<< ==")
                                                        const isChecked = selectAllActive ? filteredIdSet.has(recipient.id) : selectedIdSet.has(recipient.id)
                                                        const pushEnabled = hasRealPushToken(recipient.deviceToken)

                                                        return (
                                                            <div className="col" key={recipient.id}>
                                                                <div className="card h-100 py-3 px-2 shadow">
                                                                    <div className="d-flex align-items-start justify-content-between">
                                                                        <label className="d-flex align-items-start gap-2 cursor-pointer" htmlFor={`events-recipient-${recipient.id}`}>
                                                                            <div className="d-flex flex-column  justify-content-center">
                                                                                <span className="symbol symbol-30px symbol-circle">
                                                                                    <span className="symbol-label bg-light-primary text-primary fw-semibold fs-8">{getInitials(recipient.fullName)}</span>
                                                                                </span>
                                                                                <span className="badge badge-light-secondary mt-1 fs-8">ID: {recipient.id}</span>
                                                                            </div>

                                                                            <div className="d-flex flex-column max-w-200">
                                                                                <span className="fw-semibold text-gray-800 lh-sm" style={{ fontSize: '0.9rem' }}>
                                                                                    {recipient.fullName}
                                                                                </span>
                                                                                <div className="d-flex justify-content-between text-gray-600 mt-1">
                                                                                    <div className="small">{recipient.email || '—'}</div>
                                                                                </div>
                                                                                <div className="d-flex justify-content-between text-gray-600">
                                                                                    <div className="small">{recipient.phone ?? '—'}</div>
                                                                                </div>
                                                                            </div>
                                                                        </label>

                                                                        <div className="form-check form-check-sm form-check-custom form-check-solid ms-2">
                                                                            <input className="form-check-input" type="checkbox" id={`events-recipient-${recipient.id}`} checked={isChecked} onChange={() => handleRecipientToggle(recipient.id)} />
                                                                        </div>
                                                                    </div>

                                                                    <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                                                        {recipient.category && <span className="badge badge-light fs-8">{recipient.category}</span>}
                                                                        <span className={`badge badge-light-${recipient.isVerified ? 'success' : 'secondary'} fs-8`}>{recipient.isVerified && 'Verified'}</span>
                                                                        <span className={`badge ${pushEnabled ? 'badge-light-success' : 'badge-light-secondary'} fs-8`}>{pushEnabled ? 'Push' : 'No push'}</span>
                                                                        {recipient.hasUnits ? <span className="badge badge-light-info fs-8">Has units</span> : null}
                                                                        {recipient.blocked ? <span className="badge badge-light-danger fs-8">Blocked</span> : null}
                                                                    </div>

                                                                    {recipient.blockedReason ? (
                                                                        <div className="mt-2 small text-danger">
                                                                            Reason: {recipient.blockedReason}
                                                                        </div>
                                                                    ) : null}

                                                                    <div className="mt-3 d-flex justify-content-end">
                                                                        <button
                                                                            type="button"
                                                                            className={`btn btn-sm ${recipient.blocked ? 'btn-light-success' : 'btn-light-danger'}`}
                                                                            onClick={() => void handleToggleBlockedState(recipient)}
                                                                            disabled={blockingUserId === recipient.id || status === 'sending' || hasUnfinishedRun}
                                                                        >
                                                                            {blockingUserId === recipient.id ? (recipient.blocked ? 'Unblocking...' : 'Blocking...') : (recipient.blocked ? 'Unblock User' : 'Block User')}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {/* PUSH FIELDS */}
                                        <div className="mb-8 border rounded p-4">
                                            <div className="form-check form-switch form-check-custom form-check-solid">
                                                <input className="form-check-input" type="checkbox" id="events-send-push" checked={sendPushAlso} onChange={(e) => setSendPushAlso(e.target.checked)} />
                                                <label className="form-check-label" htmlFor="events-send-push">
                                                    Send push also
                                                </label>
                                            </div>
                                        </div>

                                        <div className="mb-8">
                                            <label className="form-label fw-semibold">
                                                Title{sendPushAlso && <span className="text-danger ms-1">*</span>}
                                            </label>
                                            <input className="form-control form-control-lg" type="text" value={title} placeholder="e.g., Maintenance alert" onChange={(event) => setTitle(event.target.value)} autoComplete="off" disabled={!sendPushAlso} />
                                        </div>

                                        <div className="mb-8">
                                            <label className="form-label fw-semibold">
                                                Description{sendPushAlso && <span className="text-danger ms-1">*</span>}
                                            </label>
                                            <textarea className="form-control" rows={4} value={description} placeholder="Describe what will happen and why it matters." onChange={(event) => setDescription(event.target.value)} disabled={!sendPushAlso} />
                                        </div>

                                        <div className="mb-8">
                                            <label className="form-label fw-semibold">Deep link (route)</label>
                                            <input className="form-control" type="text" value={deepLink} placeholder="/agent/Units/nav" onChange={(event) => setDeepLink(event.target.value)} autoComplete="off" disabled={!sendPushAlso} />
                                            <div className="form-text">Optional path the user will navigate to after clicking.</div>
                                        </div>

                                        {/* EMAIL BLOCK */}
                                        <div className="mb-8 border rounded p-4">
                                            <div className="form-check form-switch form-check-custom form-check-solid mb-3">
                                                <input className="form-check-input" type="checkbox" id="events-send-email" checked={sendEmailAlso} onChange={(e) => onToggleEmail(e.target.checked)} />
                                                <label className="form-check-label" htmlFor="events-send-email">
                                                    Send email also
                                                </label>
                                            </div>

                                            {sendEmailAlso && (
                                                <>
                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <label className="form-label fw-semibold">Email type</label>
                                                            <select className="form-select" value={emailKind} onChange={(e) => onChangeEmailKind(e.target.value as EmailKind)}>
                                                                <option value="universal">Universal Email Event</option>
                                                                <option value="unit_update">Unit update Event</option>
                                                                <option value="drop_price">DropPrice (Active Ads)</option>
                                                                <option value="account_blocked">Account blocking</option>
                                                                <option value="account_unblocked">Account unblocking</option>
                                                                <option value="trial">GIFT(Premium 30-d Free)</option>
                                                                <option value="releaseV2Warm">Release V2 - Heads Up Event</option>
                                                                <option value="basicReleaseV2">Release V2 - Main Event</option>

                                                            </select>
                                                            <div className="form-text">Trial will prefill push fields + grant trial on send.</div>
                                                        </div>

                                                        <div className="col-md-8">
                                                            <label className="form-label fw-semibold">Email template ID (readonly)</label>
                                                            <input className="form-control" type="text" value={emailTemplateId} readOnly />
                                                            <div className="form-text">Derived from selected email type.</div>
                                                        </div>

                                                        <div className="col-md-6">
                                                            <label className="form-label fw-semibold">Email deepLink</label>
                                                            <input className="form-control" type="text" value={emailDeepLink} onChange={(e) => setEmailDeepLink(e.target.value)} placeholder="/agent" />
                                                            <div className="form-text">Email deepLink (independent from push deepLink).</div>
                                                        </div>

                                                        <div className="col-md-6">
                                                            <label className="form-label fw-semibold">CTA label</label>
                                                            <input className="form-control" type="text" value={emailCtaLabel} onChange={(e) => setEmailCtaLabel(e.target.value)} placeholder="Open" />
                                                        </div>

                                                        {emailKind === 'trial' && (
                                                            <div className="col-md-6">
                                                                <label className="form-label fw-semibold">Trial days</label>
                                                                <input className="form-control" type="number" value={emailTrialDays} min={1} onChange={(e) => setEmailTrialDays(Number(e.target.value))} />
                                                                <div className="form-text">Used in email JSON as trialDays. Trial will be granted on send.</div>
                                                            </div>
                                                        )}

                                                        {/* {emailKind === 'trial' && (
                                                            <>
                                                                <div className="col-12">
                                                                    <label className="form-label fw-semibold">Trial email title</label>
                                                                    <input className="form-control" type="text" value={emailTitle} onChange={(e) => setEmailTitle(e.target.value)} />
                                                                </div>
                                                                <div className="col-12">
                                                                    <label className="form-label fw-semibold">Trial email body</label>
                                                                    <textarea className="form-control" rows={6} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} />
                                                                </div>
                                                            </>
                                                        )} */}

                                                        {emailKind === 'universal' && (
                                                            <>
                                                                <div className="col-12">
                                                                    <label className="form-label fw-semibold">Email title</label>
                                                                    <input className="form-control" type="text" value={emailTitle} onChange={(e) => setEmailTitle(e.target.value)} placeholder="Subject/title for universal template" />
                                                                </div>
                                                                <div className="col-12">
                                                                    <label className="form-label fw-semibold">Email body</label>
                                                                    <textarea className="form-control" rows={6} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} placeholder="Email description/body for universal template" />
                                                                    <div className="form-text">Independent from push description (usually longer).</div>
                                                                </div>
                                                            </>
                                                        )}

                                                        {emailKind === 'unit_update' && (
                                                            <>
                                                                <div className="col-12">
                                                                    <label className="form-label fw-semibold">Select unit (active)</label>
                                                                    <select className="form-select" value={selectedUnitId} onChange={(e) => setSelectedUnitId(e.target.value)}>
                                                                        <option value="">— Select unit —</option>
                                                                        {unitOptions.map((o) => (
                                                                            <option key={o.value} value={o.value}>
                                                                                {o.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <div className="form-text">
                                                                        This will auto-fill <code>unitAddress</code>. You can still edit it below.
                                                                    </div>
                                                                </div>

                                                                <div className="col-12">
                                                                    <label className="form-label fw-semibold">Unit address</label>
                                                                    <input className="form-control" type="text" value={emailUnitAddress} onChange={(e) => setEmailUnitAddress(e.target.value)} placeholder="e.g. Marina Gate 2, Apt 1203" />
                                                                    <div className="form-text">Used in unit_update template (unitAddress).</div>
                                                                </div>

                                                                <div className="col-12">
                                                                    <label className="form-label fw-semibold">Admin message (email)</label>
                                                                    <textarea className="form-control" rows={6} value={emailAdminMessage} onChange={(e) => setEmailAdminMessage(e.target.value)} placeholder="This is the email message content (NOT push description)." />
                                                                    <div className="form-text">
                                                                        Important: unit_update uses <code>adminMessage</code> from this field, not from push description.
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}

                                                        {emailKind === 'drop_price' && (
                                                            <>
                                                                <div className="col-12">
                                                                    <label className="form-label fw-semibold">Select unit (active)</label>
                                                                    <select className="form-select" value={selectedUnitId} onChange={(e) => setSelectedUnitId(e.target.value)}>
                                                                        <option value="">— Select unit —</option>
                                                                        {unitOptions.map((o) => (
                                                                            <option key={o.value} value={o.value}>
                                                                                {o.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <div className="form-text">
                                                                        Recipients are resolved automatically from active advertisements for this unit.
                                                                    </div>
                                                                    {dropPriceRecipientsLoading && <div className="text-muted fs-8 mt-1">Resolving active advertisements...</div>}
                                                                    {dropPriceRecipientsError && <div className="text-danger fs-8 mt-1">{dropPriceRecipientsError}</div>}
                                                                    {dropPriceRecipientsStats && !dropPriceRecipientsError && (
                                                                        <div className="text-muted fs-8 mt-1">
                                                                            Active advertisements: {dropPriceRecipientsStats.activeAds} / Total records: {dropPriceRecipientsStats.totalAds}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="col-md-6">
                                                                    <label className="form-label fw-semibold">Unit address</label>
                                                                    <input className="form-control" type="text" value={emailUnitAddress} onChange={(e) => setEmailUnitAddress(e.target.value)} placeholder="e.g. Marina Gate 2, Apt 1203" />
                                                                </div>

                                                                <div className="col-md-3">
                                                                    <label className="form-label fw-semibold">Old Price</label>
                                                                    <input className="form-control" type="text" value={emailUnitPrice} onChange={(e) => setEmailUnitPrice(e.target.value)} placeholder="e.g. 120000" />
                                                                </div>

                                                                <div className="col-md-3">
                                                                    <label className="form-label fw-semibold">New Authorized Price</label>
                                                                    <input className="form-control" type="text" value={emailUnitDiscountPrice} onChange={(e) => setEmailUnitDiscountPrice(e.target.value)} placeholder="e.g. 110000" />
                                                                </div>

                                                                <div className="col-12">
                                                                    <label className="form-label fw-semibold">Additional admin message (optional)</label>
                                                                    <textarea className="form-control" rows={5} value={emailAdminMessage} onChange={(e) => setEmailAdminMessage(e.target.value)} placeholder="Optional additional compliance note." />
                                                                    <div className="form-text">Will be injected as <code>adminMessage</code>. Template works without it.</div>
                                                                </div>
                                                            </>
                                                        )}

                                                        {emailKind === 'account_blocked' && (
                                                            <>
                                                                <div className="col-12">
                                                                    <label className="form-label fw-semibold">Block reason / admin message</label>
                                                                    <textarea className="form-control" rows={5} value={emailAdminMessage} onChange={(e) => setEmailAdminMessage(e.target.value)} placeholder="Explain why the account was blocked." />
                                                                    <div className="form-text">Required. Sent as <code>adminMessage</code> and stored as <code>blockedReason</code> in block action flow.</div>
                                                                </div>

                                                                <div className="col-md-4">
                                                                    <label className="form-label fw-semibold">Support email</label>
                                                                    <input className="form-control" type="text" value={emailSupportEmail} onChange={(e) => setEmailSupportEmail(e.target.value)} placeholder={DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL} />
                                                                </div>

                                                                <div className="col-md-4">
                                                                    <label className="form-label fw-semibold">Support phone</label>
                                                                    <input className="form-control" type="text" value={emailSupportPhone} onChange={(e) => setEmailSupportPhone(e.target.value)} placeholder={DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE} />
                                                                </div>
                                                            </>
                                                        )}

                                                        {emailKind === 'account_unblocked' && (
                                                            <>
                                                                <div className="col-md-6">
                                                                    <label className="form-label fw-semibold">Support email</label>
                                                                    <input className="form-control" type="text" value={emailSupportEmail} onChange={(e) => setEmailSupportEmail(e.target.value)} placeholder={DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL} />
                                                                </div>

                                                                <div className="col-md-6">
                                                                    <label className="form-label fw-semibold">Support phone</label>
                                                                    <input className="form-control" type="text" value={emailSupportPhone} onChange={(e) => setEmailSupportPhone(e.target.value)} placeholder={DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE} />
                                                                </div>
                                                            </>
                                                        )}

                                                        <div className="col-12">
                                                            <div className="form-check form-switch form-check-custom form-check-solid">
                                                                <input className="form-check-input" type="checkbox" id="events-email-autofill" checked={emailAutoFill} onChange={(e) => setEmailAutoFill(e.target.checked)} />
                                                                <label className="form-check-label" htmlFor="events-email-autofill">
                                                                    Auto-fill JSON from email fields
                                                                </label>
                                                            </div>
                                                            <div className="form-text">If you edit JSON manually, turn this off (or it will keep updating).</div>
                                                        </div>

                                                        <div className="col-12">
                                                            <label className="form-label fw-semibold">Dynamic template data (JSON)</label>
                                                            <textarea
                                                                className="form-control"
                                                                rows={8}
                                                                value={emailDynamicData}
                                                                onChange={(e) => {
                                                                    setEmailDynamicData(e.target.value)
                                                                    setEmailAutoFill(false)
                                                                }}
                                                                placeholder='{"deepLink":"/agent","ctaLabel":"Open"}'
                                                            />
                                                            <div className="form-text">
                                                                This object will be passed as <code>dynamicTemplateData</code>. Per-recipient fields (<code>firstName</code>/<code>fullName</code>) are injected automatically on send.
                                                            </div>
                                                        </div>

                                                        {sendEmailAlso && finalEmailPreview && (
                                                            <div className="mt-4 border rounded p-4 bg-light">
                                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                                    <span className="text-muted text-uppercase fs-8">
                                                                        Final email preview (what user will receive)
                                                                    </span>
                                                                    <span className="badge badge-light-primary fs-8">
                                                                        {previewRecipient?.fullName ?? '—'}
                                                                    </span>
                                                                </div>

                                                                <div className="mb-3">
                                                                    <div className="text-muted fs-8 text-uppercase">Subject</div>
                                                                    <div className="fw-semibold">
                                                                        {finalEmailPreview.subject || '—'}
                                                                    </div>
                                                                </div>

                                                                <div className="text-muted fs-8 text-uppercase">Body</div>
                                                                <pre
                                                                    className="mb-0"
                                                                    style={{
                                                                        whiteSpace: 'pre-wrap',
                                                                        fontFamily: 'inherit',
                                                                        fontSize: '0.9rem',
                                                                    }}
                                                                >
                                                                    {finalEmailPreview.body || '—'}
                                                                </pre>
                                                            </div>
                                                        )}


                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {validationError && (
                                            <div className="alert alert-danger d-flex align-items-center mb-6">
                                                <span className="svg-icon svg-icon-2hx svg-icon-danger me-3">
                                                    <i className="bi bi-exclamation-triangle" />
                                                </span>
                                                <span>{validationError}</span>
                                            </div>
                                        )}

                                        <div className="d-flex flex-wrap justify-content-end gap-3">
                                            <button type="button" className="btn btn-light" onClick={handleResetForm} disabled={status === 'sending' || hasUnfinishedRun}>
                                                Reset
                                            </button>
                                            <button type="submit" className="btn btn-bso" disabled={status === 'sending' || hasUnfinishedRun}>
                                                {status === 'sending' ? 'Processing…' : hasUnfinishedRun ? 'Run in progress' : 'Confirm & Send'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* SUMMARY */}
                        <div className="col-xxl-4">
                            <div className="card card-flush h-100 shadow">
                                <div className="card-header border-0 pt-6">
                                    <h3 className="card-title fw-semibold mb-0">Summary preview</h3>
                                </div>

                                <div className="card-body pt-0">
                                    <div className="border border-dashed border-gray-300 rounded p-4 mb-5">
                                        <div className="mb-4">
                                            <span className="text-muted text-uppercase fs-8 d-block">Recipients</span>
                                            <span className="fw-semibold text-gray-800">{recipientsPreview}</span>
                                            {selectAllActive && <div className="fs-8 text-muted">Selection covers entire filtered list.</div>}
                                        </div>

                                        <div className="mb-4">
                                            <span className="text-muted text-uppercase fs-8 d-block">Push Title</span>
                                            <span className="fw-semibold text-gray-800">{sendPushAlso ? title || '—' : 'Push sending disabled'}</span>
                                        </div>

                                        <div className="mb-4">
                                            <span className="text-muted text-uppercase fs-8 d-block">Push Description</span>
                                            <span className="text-gray-800">{sendPushAlso ? description || '—' : 'Push sending disabled'}</span>
                                        </div>

                                        <div className="mb-4">
                                            <span className="text-muted text-uppercase fs-8 d-block">Push Deep link</span>
                                            <span className="text-gray-800">{sendPushAlso ? defaultRoute : '—'}</span>
                                        </div>

                                        <div className="mb-4">
                                            <span className="text-muted text-uppercase fs-8 d-block">Delivery</span>
                                            <span className="text-gray-800">
                                                {!sendPushAlso
                                                    ? 'Push disabled.'
                                                    : deliveryMode === 'immediate'
                                                        ? 'Send immediately after confirmation.'
                                                        : scheduledDate != null
                                                            ? `Scheduled for ${formatDateTimeForTable(scheduledDate.toISOString())}`
                                                            : 'Schedule time is not set.'}
                                            </span>
                                            {sendPushAlso && deliveryMode === 'scheduled' && relativePreview && <div className="fs-8 text-muted">{relativePreview}</div>}
                                        </div>

                                        <div className="mb-2">
                                            <span className="text-muted text-uppercase fs-8 d-block">Email</span>
                                            <span className="text-gray-800">{sendEmailAlso ? `Will send email (${emailKind})` : 'Email sending disabled'}</span>
                                            {sendEmailAlso && (
                                                <div className="fs-8 text-muted">
                                                    Template: <span className="fw-semibold">{emailTemplateId}</span> · Auto-fill: {emailAutoFill ? 'ON' : 'OFF'}
                                                    {emailKind === 'trial' ? ` · Trial grant: ON (${emailTrialDays} days)` : ''}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Email preview */}
                                    {sendEmailAlso && (
                                        <div className="border rounded p-4 mb-5">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="text-muted text-uppercase fs-8">Email preview</span>
                                                <span className="badge badge-light-secondary fs-8">{previewRecipient ? `Using: ${previewRecipient.fullName}` : 'No recipient'}</span>
                                            </div>

                                            <div className="mb-3">
                                                <div className="text-muted fs-8 text-uppercase">Subject</div>
                                                <div className="fw-semibold">{emailPreview?.subject || '—'}</div>
                                            </div>

                                            <div className="text-muted fs-8 text-uppercase">Body</div>
                                            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.9rem' }}>
                                                {emailPreview?.body || '—'}
                                            </pre>
                                        </div>
                                    )}

                                    {!summaryReady && <div className="alert alert-warning">Fill in the required fields, apply filters, and select recipients.</div>}

                                    {status === 'sending' && progress.total > 0 && (
                                        <div className="mb-5">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className="text-muted fs-8 text-uppercase">Sending progress</span>
                                                <span className="fw-semibold fs-8">
                                                    {progress.done} / {progress.total}
                                                </span>
                                            </div>
                                            <div className="progress h-6px">
                                                <div
                                                    className="progress-bar bg-primary"
                                                    role="progressbar"
                                                    style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                                                    aria-valuenow={Math.round((progress.done / progress.total) * 100)}
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {status === 'sending' && (
                                        <div>
                                            Sending notifications… Please wait.
                                            {progress.total > 0 && <span className="ms-2">({progress.done} / {progress.total})</span>}
                                        </div>
                                    )}
                                    {status === 'success' && <div className="alert alert-success">{statusDetails || 'Sending completed.'}</div>}
                                    {status === 'error' && <div className="alert alert-danger">{statusDetails || 'An error occurred during sending.'}</div>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* HISTORY */}
                    <div className="card card-flush mt-10 shadow">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <h3 className="fw-semibold mb-0">Backend history</h3>
                                <span className="badge badge-light fs-8 ms-3">{history.length}</span>
                            </div>
                        </div>

                        <div className="card-body pt-0">
                            {historyLoading ? (
                                <div className="text-center text-muted py-10">Loading backend history…</div>
                            ) : history.length === 0 ? (
                                <div className="text-center text-muted py-10">No runs in backend history yet.</div>
                            ) : (
                                <div>
                                    <table className="table align-middle table-row-dashed gy-3">
                                        <thead>
                                            <tr className="text-start text-gray-500 fw-semibold text-uppercase fs-8">
                                                <th>Event</th>
                                                <th>Recipients</th>
                                                <th>Created</th>
                                                <th>Scheduled</th>
                                                <th>Status</th>
                                                <th>Route</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {history.map((item) => {
                                                const pushTitle = (item.payload.push.title ?? '').trim()
                                                const pushDescription = (item.payload.push.description ?? '').trim()
                                                const hasPushText = pushTitle.length > 0 || pushDescription.length > 0
                                                const eventTitle = hasPushText ? pushTitle || 'Push notification' : `Email (${item.payload.email.kind})`
                                                const eventDescription = hasPushText
                                                    ? pushDescription || '�'
                                                    : `Template: ${item.payload.email.templateId || '�'}`

                                                return (
                                                    <tr key={item.runId}>
                                                        <td>
                                                            <div className="fw-semibold text-gray-800">{eventTitle}</div>
                                                            <div className="fs-8 text-muted">{eventDescription}</div>
                                                        </td>
                                                        <td className="text-gray-600">{item.recipients.label}</td>
                                                        <td className="text-gray-600">{formatDateTimeForTable(item.createdAt)}</td>
                                                        <td className="text-gray-600">{formatDateTimeForTable(item.payload.push.scheduledFor)}</td>
                                                        <td>
                                                            <span className={`badge badge-light-${statusBadgeVariantSafe[item.status]}`}>{statusLabelSafe[item.status]}</span>
                                                            {item.statusMessage && <div className="fs-8 text-muted mt-1">{item.statusMessage}</div>}
                                                        </td>
                                                        <td className="text-gray-600">{item.payload.push.deepLink ?? '�'}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    )
}




